/**
 * Contrato entre el navegador y el Worker para la pestaña Rastreo.
 *
 * Los datos salen del motor propio (`drive/rastreo/motor`), pero el navegador
 * nunca habla con él: el Worker lo consulta con sus credenciales y entrega esto.
 * Son estructuras neutrales —coordenadas, horas y estados—, sin objetos de
 * Google ni de Mapbox, para que cambiar de proveedor de mapa no toque los datos
 * (especificación de mapas, § 5.1).
 *
 * `null` no es cero: velocidad `null` es «el equipo no la mandó», no «quieto».
 * Conectividad y frescura son señales distintas: un equipo puede estar en línea
 * mandando solo latidos, con la posición vieja.
 */

import { z } from "zod";

export const connectionStatusSchema = z.enum(["online", "offline", "unknown"]);
export const telemetryFreshnessSchema = z.enum(["fresh", "stale", "unknown"]);
export const ignitionSchema = z.enum(["on", "off", "unknown"]);
export const clockQualitySchema = z.enum(["ok", "unverified", "drift", "unknown"]);
export const modelStatusSchema = z.enum(["en_prueba", "soportado", "no_soportado"]);

/** Atributos tal como los publica el motor: batería, voltajes, GSM, odómetro… */
export const rastreoAttributesSchema = z.record(
  z.string(),
  z.union([z.number(), z.boolean(), z.string(), z.null()]),
);

export const rastreoPositionSchema = z.object({
  kind: z.enum(["position", "alarm"]),
  fixValid: z.boolean().nullable(),
  latitude: z.number(),
  longitude: z.number(),
  altitudeM: z.number().nullable(),
  speedKph: z.number().nullable(),
  headingDeg: z.number().nullable(),
  satellites: z.number().nullable(),
  ignition: ignitionSchema,
  deviceTime: z.string().nullable(),
  serverReceivedAt: z.string(),
  storedAt: z.string(),
  clockQuality: clockQualitySchema,
  attributes: rastreoAttributesSchema,
});
export type RastreoPosition = z.infer<typeof rastreoPositionSchema>;

export const rastreoVehicleSchema = z.object({
  /** Referencia opaca del motor. El IMEI nunca llega al navegador. */
  deviceId: z.string(),
  name: z.string().nullable(),
  /** Últimos 4 dígitos del IMEI (D28 del motor); `null` con un motor que todavía no los manda. */
  identifierTail: z.string().max(4).nullable(),
  model: z.string().nullable(),
  modelStatus: modelStatusSchema,
  sourceProtocol: z.string().nullable(),
  connectionStatus: connectionStatusSchema,
  lastConnectionAt: z.string().nullable(),
  telemetryFreshness: telemetryFreshnessSchema,
  lastTelemetryAt: z.string().nullable(),
  /** `null` si el equipo todavía no mandó ninguna posición válida. */
  lastPosition: rastreoPositionSchema.nullable(),
});
export type RastreoVehicle = z.infer<typeof rastreoVehicleSchema>;

export const rastreoHealthSchema = z.object({
  devices: z.object({
    active: z.number(),
    online: z.number(),
    freshTelemetry: z.number(),
  }),
  frames: z.object({
    lastHour: z.number(),
    invalidLastHour: z.number(),
    quarantinedLastDay: z.number(),
    /**
     * Tramas de equipos registrados con un tipo que el motor no sabe leer, últimas 24 h (D32 del
     * motor). Mayor que 0: un modelo manda algo que falta agregar al decodificador. Opcional
     * porque un motor anterior no lo manda: el orden de despliegue no rompe nada.
     */
    undecodedLastDay: z.number().optional(),
    lastReceivedAt: z.string().nullable(),
  }),
  thresholds: z.object({
    connectionS: z.number(),
    freshnessS: z.number(),
  }),
});
export type RastreoHealth = z.infer<typeof rastreoHealthSchema>;

export const rastreoFleetResponseSchema = z.object({
  generatedAt: z.string(),
  health: rastreoHealthSchema,
  vehicles: z.array(rastreoVehicleSchema),
  /** `false` si se cortó el inventario por el tope de equipos de esta vista. */
  complete: z.boolean(),
});
export type RastreoFleetResponse = z.infer<typeof rastreoFleetResponseSchema>;

export const rastreoTrackResponseSchema = z.object({
  deviceId: z.string(),
  from: z.string(),
  to: z.string(),
  points: z.array(rastreoPositionSchema),
  /** `true` si había más puntos que el tope y el recorrido se recortó. */
  truncated: z.boolean(),
});
export type RastreoTrackResponse = z.infer<typeof rastreoTrackResponseSchema>;

export const rastreoEventSchema = z.object({
  type: z.string(),
  time: z.string(),
  hasPosition: z.boolean(),
});
export type RastreoEvent = z.infer<typeof rastreoEventSchema>;

export const rastreoEventsResponseSchema = z.object({
  deviceId: z.string(),
  from: z.string(),
  to: z.string(),
  events: z.array(rastreoEventSchema),
  truncated: z.boolean(),
});
export type RastreoEventsResponse = z.infer<typeof rastreoEventsResponseSchema>;

/**
 * Qué mapa usar en la web. La clave de Google es pública por diseño (viaja al
 * navegador en cualquier integración de Maps JavaScript): se protege
 * restringiéndola a los dominios de Drive, no escondiéndola.
 */
export const rastreoMapConfigSchema = z.discriminatedUnion("provider", [
  z.object({ provider: z.literal("google"), googleMapsKey: z.string().min(1) }),
  z.object({
    provider: z.literal("none"),
    /** Por qué no hay mapa, en palabras para la pantalla. */
    reason: z.string(),
  }),
]);
export type RastreoMapConfig = z.infer<typeof rastreoMapConfigSchema>;

/** Período máximo de un recorrido o de una lista de alarmas: el mismo tope del motor. */
export const RASTREO_MAX_PERIOD_DAYS = 7;
